"use client";

import * as React from "react";

import { usePopup, type UsePopupOptions } from "@/hooks/popup";
import type { FetchApiError, StrapiCollectionResponse, StrapiEntity } from "@/lib/strapi";
import type { PopupAttributes } from "@/services/popup";

export type PopupStoreValue = {
  popup: StrapiEntity<PopupAttributes> | null;
  data: StrapiCollectionResponse<PopupAttributes> | null;
  error: FetchApiError | null;
  isLoading: boolean;
  refetch: () => void;
};

const PopupStoreContext = React.createContext<PopupStoreValue | null>(null);

export function PopupProvider({
  children,
  initialData,
  ...options
}: React.PropsWithChildren<
  UsePopupOptions & { initialData?: StrapiCollectionResponse<PopupAttributes> | null }
>): React.JSX.Element {
  const state = usePopup({ ...options, initialData });

  const value: PopupStoreValue = {
    popup: state.popup,
    data: state.data,
    error: state.error,
    isLoading: state.isLoading,
    refetch: state.refetch,
  };

  return <PopupStoreContext.Provider value={value}>{children}</PopupStoreContext.Provider>;
}

export function usePopupStore(): PopupStoreValue {
  const value = React.useContext(PopupStoreContext);
  if (!value) {
    throw new Error("usePopupStore must be used within PopupProvider");
  }
  return value;
}
